import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { publicApi } from "../../utils/publicApi";
import SiteHeader from "../../components/SiteHeader";
import SiteFooter from "../../components/SiteFooter";
import "./PublicCms.css";

const PublicFaqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    document.title = "FAQs — AlphaXCloud";
    (async () => {
      try {
        const res = await publicApi({ url: "admin/get-all-faq" });
        if (res.success) {
          const rows = res.data || [];
          setFaqs(rows);
          if (rows[0]) setOpenId(rows[0].id);
        } else setError(res.msg || "Could not load FAQs");
      } catch {
        setError("Could not load FAQs");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (f) =>
        String(f.que || "").toLowerCase().includes(q) ||
        String(f.ans || "").toLowerCase().includes(q)
    );
  }, [faqs, query]);

  return (
    <div className="public-cms faq-page site-theme">
      <SiteHeader />

      <main className="faq-main">
        <header className="faq-hero">
          <p className="faq-eyebrow">Help center</p>
          <h1>Answers before you get stuck</h1>
          <p className="faq-lead">
            Clear guidance on sessions, campaigns, limits, and API access — so you can keep
            messaging without friction.
          </p>
          <div className="faq-search">
            <Icon icon="mdi:magnify" width={20} />
            <input
              type="search"
              placeholder="Search questions…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search FAQs"
            />
          </div>
        </header>

        {loading ? <p className="public-cms-muted faq-status">Loading…</p> : null}
        {error ? <div className="public-cms-error">{error}</div> : null}

        {!loading && !error && faqs.length === 0 ? (
          <p className="public-cms-muted faq-status">
            No FAQs yet. Add some in Admin → FAQs.
          </p>
        ) : null}

        {!loading && !error && faqs.length > 0 && filtered.length === 0 ? (
          <p className="public-cms-muted faq-status">No matches for “{query.trim()}”.</p>
        ) : null}

        <div className="faq-layout">
          <aside className="faq-aside">
            <h2>Need more help?</h2>
            <p>
              Browse stories from other teams, or jump into your workspace once you’re ready
              to connect a session.
            </p>
            <Link className="faq-aside-link" to="/testimonials">
              Read testimonials
              <Icon icon="mdi:arrow-right" width={18} />
            </Link>
            <Link className="faq-aside-link faq-aside-link-soft" to="/register">
              Create account
              <Icon icon="mdi:arrow-right" width={18} />
            </Link>
          </aside>

          <div className="faq-list">
            {filtered.map((item, i) => {
              const open = openId === item.id;
              return (
                <article
                  key={item.id}
                  className={`faq-item ${open ? "is-open" : ""}`}
                  style={{ animationDelay: `${Math.min(i, 10) * 0.05}s` }}
                >
                  <button
                    type="button"
                    className="faq-item-trigger"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : item.id)}
                  >
                    <span className="faq-item-index">{String(i + 1).padStart(2, "0")}</span>
                    <span className="faq-item-q">{item.que}</span>
                    <span className="faq-item-icon" aria-hidden="true">
                      <Icon icon={open ? "mdi:minus" : "mdi:plus"} width={20} />
                    </span>
                  </button>
                  <div className="faq-item-body" hidden={!open}>
                    <p>{item.ans}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PublicFaqs;
